import React, { useMemo, useContext, Suspense } from 'react';
import { Switch, Redirect, Route, matchPath, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getObject } from '../util/mapObject';
import Loader from './Loader';
import { locationAsNextPath, withNextPath } from '../util/nextLocation';

function RouteSwitch({ routes }) {
  const auth = useContext(AuthContext);

  // Routes the current session may NOT see are kept as well as dropped: a
  // location that matches one of them is "you need to log in", not "no such
  // page", and only the first deserves to be sent back here afterwards.
  const [allowedRoutes, blockedRoutes] = useMemo(() => {
    const allowed = [];
    const blocked = [];

    Object.entries(routes).forEach((entry) => {
      (checkRequirements(auth, entry[1].requires) ? allowed : blocked).push(entry);
    });

    return [allowed, blocked];
  }, [auth]);

  const defaultRoute = useMemo(() => allowedRoutes.filter(entry => entry[1].default).sort((a, b) => b[1].default - a[1].default)[0], [allowedRoutes]);

  return (
    <Suspense fallback={<Loader fullscreen />}>
      <Switch>
        {allowedRoutes.map(([path, { ...route }], idx) => (
          <Route key={idx} path={path} {...route} />
        ))}
        {defaultRoute ? <DefaultRedirect to={defaultRoute[0]} blockedRoutes={blockedRoutes} /> : null}
      </Switch>
    </Suspense>
  );
}

/**
 * Where a location no allowed route matched ends up.
 *
 * When it matches a route this session is not allowed to see, the attempted URL
 * travels along as `?next=` so the login flow can hand the person back to it —
 * see util/nextLocation. A location that matches nothing at all is a plain
 * typo and is redirected without it.
 */
function DefaultRedirect({ to, blockedRoutes }) {
  const location = useLocation();

  const needsLogin = blockedRoutes.some(
    ([path, route]) => !!matchPath(location.pathname, { path, exact: route.exact })
  );

  if (!needsLogin) return <Redirect to={to} />;

  return <Redirect to={withNextPath(to, locationAsNextPath(location))} />;
}

function checkRequirements({ user }, requires) {
  if (requires === 'login') {
    return !!user;
  } else if (typeof requires === 'string') {
    return !!getObject(user, requires);
  }

  return true;
}

export default RouteSwitch;
